import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { Text, tokens } from '@/design';

/**
 * Live 3D preview of a 3D Tiles asset (buildings, oil tank, reality mesh, …).
 *
 * The 2D footprint map answers "where does it sit"; this answers "what does it
 * look like" — the user's actual ask. It streams the tileset with the same
 * `3d-tiles-renderer` + Draco setup the main Giro3D scene uses, frames it on a
 * slowly-rotating turntable, and is fully self-contained (own WebGL context,
 * own RAF, disposed on unmount). It never touches the live viewer.
 *
 * The preview is engine-light on purpose: top-down-agnostic orbit, default SSE,
 * graceful fallbacks. If WebGL is unavailable or no geometry streams in, it
 * degrades to a caption rather than throwing — the footprint map still renders.
 */

const DRACO_DECODER_PATH = 'https://www.gstatic.com/draco/v1/decoders/';

type PreviewStatus = 'loading' | 'ready' | 'empty' | 'error';

interface LayerPreview3DProps {
  readonly tilesetUrl: string;
  readonly height?: number;
}

export function LayerPreview3D({ tilesetUrl, height = 240 }: LayerPreview3DProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<PreviewStatus>('loading');
  const [detail, setDetail] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !tilesetUrl) return;

    let disposed = false;
    let raf = 0;
    let renderer: THREE.WebGLRenderer | null = null;
    let tiles: { dispose: () => void; group: THREE.Object3D } | null = null;
    let draco: { dispose: () => void } | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let emptyTimer: ReturnType<typeof setTimeout> | null = null;
    let lastError: string | null = null;

    const start = async () => {
      // --- three.js scaffold ---------------------------------------------
      const initialW = container.clientWidth || 320;
      const initialH = container.clientHeight || height;
      try {
        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
      } catch {
        if (!disposed) setStatus('error');
        return;
      }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      renderer.setSize(initialW, initialH, false);
      const canvas = renderer.domElement;
      canvas.style.width = '100%';
      canvas.style.height = '100%';
      canvas.style.display = 'block';
      container.appendChild(canvas);

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(tokens.color.surface[0]);
      const camera = new THREE.PerspectiveCamera(50, initialW / initialH, 0.01, 1e9);
      camera.position.set(0, 0, 10);

      scene.add(new THREE.AmbientLight(0xffffff, 1.4));
      const key = new THREE.DirectionalLight(0xffffff, 1.8);
      key.position.set(1, 1.4, 1);
      scene.add(key);
      const fill = new THREE.DirectionalLight(0xa9c4ff, 0.7);
      fill.position.set(-1, 0.4, -0.8);
      scene.add(fill);

      // --- tileset streaming ---------------------------------------------
      let TilesRendererCtor: new (url: string) => typeof tiles & {
        setCamera: (c: THREE.Camera) => void;
        setResolutionFromRenderer: (c: THREE.Camera, r: THREE.WebGLRenderer) => void;
        update: () => void;
        getBoundingSphere: (s: THREE.Sphere) => boolean;
        registerPlugin: (p: unknown) => void;
        addEventListener: (t: string, cb: (e: unknown) => void) => void;
        group: THREE.Object3D;
      };
      try {
        const mod = await import('3d-tiles-renderer');
        TilesRendererCtor = mod.TilesRenderer as unknown as typeof TilesRendererCtor;
      } catch {
        if (!disposed) setStatus('error');
        return;
      }
      if (disposed) return;

      const tr = new TilesRendererCtor(tilesetUrl);
      tiles = tr;

      // Decoder support so compressed glTF tiles actually render. Mirrors the
      // main Giro3D path (gstatic Draco) and ALSO wires meshopt — many converter
      // tilesets meshopt-compress geometry, which silently produces an empty
      // (black) preview without the decoder. Best-effort: uncompressed tiles
      // still load if an import fails.
      try {
        const [{ DRACOLoader }, meshoptMod, { GLTFExtensionsPlugin }] = await Promise.all([
          import('three/examples/jsm/loaders/DRACOLoader.js'),
          import('three/examples/jsm/libs/meshopt_decoder.module.js'),
          import('3d-tiles-renderer/plugins'),
        ]);
        if (disposed) return;
        const dracoLoader = new DRACOLoader();
        dracoLoader.setDecoderPath(DRACO_DECODER_PATH);
        draco = dracoLoader as unknown as { dispose: () => void };
        tr.registerPlugin(
          new GLTFExtensionsPlugin({
            dracoLoader,
            meshoptDecoder: (meshoptMod as { MeshoptDecoder?: unknown }).MeshoptDecoder as never,
            autoDispose: true,
          }),
        );
      } catch (err) {
        console.warn('[LayerPreview3D] decoder plugin setup failed; uncompressed tiles only', err);
      }

      // Surface load failures instead of a silent black canvas — the #1 reason a
      // tileset shows in the main scene but not here is a decoder/CORS/404 the
      // preview can't satisfy.
      tr.addEventListener('load-error', (e: unknown) => {
        const ev = e as { error?: { message?: string }; url?: string | URL };
        lastError = ev?.error?.message ?? 'tile load error';
        console.warn('[LayerPreview3D] tile load error', String(ev?.url ?? tilesetUrl), ev?.error);
      });

      tr.setCamera(camera);
      tr.setResolutionFromRenderer(camera, renderer);
      scene.add(tr.group);

      // --- frame on first bounds, then turntable -------------------------
      let framed = false;
      let radius = 1;
      const sphere = new THREE.Sphere();
      // Orbit basis: `up` is the asset's local vertical, `tan1`/`tan2` span the
      // horizontal plane the camera circles in.
      const up = new THREE.Vector3(0, 1, 0);
      const tan1 = new THREE.Vector3(1, 0, 0);
      const tan2 = new THREE.Vector3(0, 0, 1);
      const tryFrame = () => {
        if (framed || disposed || !tiles) return;
        if (tr.getBoundingSphere(sphere) && Number.isFinite(sphere.radius) && sphere.radius > 0) {
          framed = true;
          const center = sphere.center.clone();
          // Re-centre the asset at the origin: 3D Tiles live in ECEF/projected
          // metres (coordinates in the millions) which jitters in float32. With
          // the asset at origin the turntable is stable and the near/far planes
          // can hug it tightly.
          tr.group.position.copy(center).multiplyScalar(-1);
          radius = sphere.radius;
          // Best-effort "up": geocentric (ECEF) tilesets — centre magnitude in
          // the millions — stand upright along the radial; projected / local
          // tilesets fall back to world +Y. Without this, buildings render lying
          // on their side.
          if (center.length() > 1e5) up.copy(center).normalize();
          else up.set(0, 1, 0);
          const ref = Math.abs(up.y) > 0.95
            ? new THREE.Vector3(1, 0, 0)
            : new THREE.Vector3(0, 1, 0);
          tan1.copy(ref).cross(up).normalize();
          tan2.copy(up).cross(tan1).normalize();
          camera.up.copy(up);
          camera.near = Math.max(radius / 1000, 0.01);
          camera.far = radius * 1000;
          camera.updateProjectionMatrix();
          if (emptyTimer) {
            clearTimeout(emptyTimer);
            emptyTimer = null;
          }
          setStatus('ready');
        }
      };
      tr.addEventListener('load-tileset', tryFrame);
      tr.addEventListener('load-content', tryFrame);
      tr.addEventListener('tiles-load-end', tryFrame);

      // If nothing has framed within a grace window, surface an empty state so
      // the user isn't left staring at a black box (the footprint still shows).
      emptyTimer = setTimeout(() => {
        if (disposed || framed) return;
        setDetail(lastError);
        setStatus(lastError ? 'error' : 'empty');
      }, 12000);

      let theta = 0.7;
      const phi = 0.62; // ~35° elevation for a readable 3/4 view
      const animate = () => {
        if (disposed || !renderer || !tiles) return;
        raf = requestAnimationFrame(animate);
        // Keep attempting to frame until bounds are available (some tilesets
        // only expose a usable bounding sphere after the first content loads,
        // and the load events alone can race the renderer).
        if (!framed) tryFrame();
        if (framed) {
          theta += 0.0042;
          const dist = radius * 2.6;
          const cosPhi = Math.cos(phi);
          const sinPhi = Math.sin(phi);
          const a = cosPhi * Math.cos(theta);
          const b = cosPhi * Math.sin(theta);
          camera.position.set(
            dist * (a * tan1.x + b * tan2.x + sinPhi * up.x),
            dist * (a * tan1.y + b * tan2.y + sinPhi * up.y),
            dist * (a * tan1.z + b * tan2.z + sinPhi * up.z),
          );
          camera.up.copy(up);
          camera.lookAt(0, 0, 0);
        }
        tr.setResolutionFromRenderer(camera, renderer);
        tr.setCamera(camera);
        tr.update();
        renderer.render(scene, camera);
      };
      animate();

      // --- responsive sizing ---------------------------------------------
      resizeObserver = new ResizeObserver(() => {
        if (disposed || !renderer) return;
        const w = container.clientWidth || initialW;
        const h = container.clientHeight || initialH;
        renderer.setSize(w, h, false);
        camera.aspect = w / Math.max(h, 1);
        camera.updateProjectionMatrix();
      });
      resizeObserver.observe(container);
    };

    void start();

    return () => {
      disposed = true;
      if (raf) cancelAnimationFrame(raf);
      if (emptyTimer) clearTimeout(emptyTimer);
      resizeObserver?.disconnect();
      try {
        tiles?.dispose();
      } catch {
        /* ignore */
      }
      try {
        draco?.dispose();
      } catch {
        /* ignore */
      }
      if (renderer) {
        try {
          renderer.dispose();
          renderer.forceContextLoss();
        } catch {
          /* ignore */
        }
        const canvas = renderer.domElement;
        if (canvas.parentElement === container) container.removeChild(canvas);
      }
    };
  }, [tilesetUrl, height]);

  return (
    <div>
      <div
        ref={containerRef}
        style={{
          position: 'relative',
          width: '100%',
          height,
          borderRadius: tokens.radius.md,
          overflow: 'hidden',
          border: tokens.border.thin,
          background: tokens.color.surface[0],
        }}
      >
        {status !== 'ready' ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: tokens.space[3],
              pointerEvents: 'none',
            }}
          >
            <Text variant="body" style={{ color: tokens.color.ink.muted, fontSize: tokens.font.size.xs }}>
              {status === 'loading'
                ? 'Streaming 3D tiles…'
                : status === 'empty'
                  ? 'No geometry streamed yet — the tileset may be large or restricted. The main scene still renders it.'
                  : `3D preview couldn't load this tileset${detail ? ` (${detail})` : ''}. The footprint below still works.`}
            </Text>
          </div>
        ) : null}
      </div>
    </div>
  );
}
